import React, { useEffect, useState } from 'react';
import { 
    Text, 
    IPanelStyles, 
    IPivotStyles, 
    Panel, 
    PanelType, 
    Pivot, 
    PivotItem, 
    Dropdown,
    IDropdownOption,
    DefaultButton,
    SearchBox,
    ISearchBoxStyles,
    mergeStyleSets,
} from '@fluentui/react';
import WorkOrderCreateComponent from '../../components/work-order/work-order-create.component';
import BreadcrumbComponent from '../../components/breadcrumb/breadcrumb.component';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import WorkOrderListComponent from '../../components/work-order/work-order-list.component';
import WorkOrderComponent from '../../components/work-order/work-order.component';
import LoaderComponent from '../../components/loader/loader.component';
import DatePickerComponent from '../../components/date-picker/date-picker.component';
import ModalDefaultComponent from '../../components/modal-default/modal-default.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import NoPermissionComponent from '../../components/no-permission/no-permission.component';
import emptyIcon from '../../assets/img48/vacio_48.png';
import { useCookies } from "react-cookie";
import queryString from "query-string";
import { useNavigate } from 'react-router-dom';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemFiltrosOrdenDeTrabajo from '../../models/ItemFiltrosOrdenDeTrabajo';
import ItemOperario from '../../models/ItemOperario';
import ElementEnum from '../../enums/ElementEnum';
import ItemPermiso from '../../models/ItemPermiso';

const pivotStyles: IPivotStyles = {
    root: {},
    linkIsSelected: {
        selectors: {
            '::before': {
                backgroundColor: "var(--color-primario)",
            },
        },
    },
    link: {},
    linkContent: {},
    text: {},
    count: {},
    icon: {},
    linkInMenu: {},
    overflowMenuButton: {},
};

const panelFiltersStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {},
    contentInner: {},
    scrollableContent: {},
    navigation: {},
    header: {},
    headerText: {},
    content: {},
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: 'rgb(50, 49, 48)',
            },
        },
    },
};

const stateOptions: IDropdownOption[] = [
    { key: 0, text: 'Todas' },
    { key: 10, text: 'Pendientes' },
    { key: 20, text: 'En curso' },
    { key: 30, text: 'Finalizadas' },
    { key: 40, text: 'Cerradas' },
];

const searchBoxStyles: ISearchBoxStyles = {
    root: {
        borderRadius: 6,
        border: '1px solid #ccc',
        width: 220,
        height: 36,
    },
};

const toolbarClasses = mergeStyleSets({
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0 4px 0',
    },
    titleRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    pivotRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    emptyState: {
        fontSize: 22,
        color: '#888',
        fontStyle: 'italic',
        textAlign: 'center' as const,
    },
    emptyIcon: {
        opacity: 0.25,
    },
});

interface IWorkOrdersPageProps {};

interface IWorkOrdersPageParams {
    id: number;
};

const WorkOrdersPage = (props: IWorkOrdersPageProps) => {
    const navigate = useNavigate();
    const auth = useAuthStatus();
    const workOrdersPermission : ItemPermiso = auth.getPermission(ElementEnum.WORK_ORDERS);

    const [cookies, setCookie] = useCookies(["work-orders-filters"]);

    const [params, setParams] = useState<IWorkOrdersPageParams>();
    const [workOrders, setWorkOrders] = useState<ItemOrdenDeTrabajo[]>([]);
    const [workOrderOpen, setWorkOrderOpen] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [operators, setOperators] = useState<ItemOperario[]>([]);
    const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
    const [filters, setFilters] = useState<ItemFiltrosOrdenDeTrabajo>(new ItemFiltrosOrdenDeTrabajo());
    const [selectedWorkOrder, setSelectedWorkOrder] = useState<ItemOrdenDeTrabajo>();
    const [loading, setLoading] = useState<boolean>(true);
    const [submit, setSubmit] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [searchText, setSearchText] = useState<string>('');

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        if (queryParams.id) {
            setParams({
                id: parseInt(queryParams.id.toString()),
            });
        }
    }, []);

    useEffect(() => {
        if (auth.user) {
            if (auth.user.ModoBienvenida) {
                navigate('/main/bienvenida/');
            }
            else if (workOrdersPermission.Ver) {
                if (auth.user.UsuarioAdmin) {
                    setLoading(true);
                    ApiService.Post<ItemOperario[]>("OrdenesTrabajo", "GetListaOperarios", auth.user).then((response: ItemOperario[]) => {
                        setOperators(response);
                        setLoading(false);
                    }, (error: string) => {
                        setError(error);
                        setLoading(false);
                    });
                }
                else {
                    let operator : ItemOperario = new ItemOperario();
                    operator.IdUsuario = auth.user.IdUsuario;
                    operator.Nombre = auth.user.Nombre;
                    setOperators([operator]);
                }
            }
        }
    }, [workOrdersPermission.Ver, auth.user, navigate]);

    useEffect(() => {
        if (operators.length > 0) {
            const ids = operators.map((o: ItemOperario) => o.IdUsuario);
            setSelectedOperatorIds(ids);
            setFilters((f: ItemFiltrosOrdenDeTrabajo) => ({
                ...f,
                Operarios: ids.join("|"),
            }));
            setSubmit(true);
        }
    }, [operators]);

    useEffect(() => {
        if (auth.user && workOrdersPermission.Ver && submit) {
            setLoading(true);
            setCookie("work-orders-filters", filters);
            sessionStorage.setItem('work-orders-filters', JSON.stringify(filters));
            setFiltersOpen(false);
            ApiService.Post<ItemOrdenDeTrabajo[]>("OrdenesTrabajo", "GetOrdenesTrabajo", auth.user, filters).then((response: ItemOrdenDeTrabajo[]) => {
                setWorkOrders(response);
                setSubmit(false);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setSubmit(false);
                setLoading(false);
            });
        }
    }, [auth.user, submit, filters, workOrdersPermission.Ver, setCookie]);

    useEffect(() => {
        if (params) {
            setSelectedWorkOrder(workOrders.find((workOrder: ItemOrdenDeTrabajo) => {
                return (workOrder.Id === params.id)
            }) || undefined);
        }
        else {
            setSelectedWorkOrder(undefined);
        }
    }, [params, workOrders]);

    const onStatusPivotChanged = (item?: PivotItem) : void => {
        if (item && item.props.itemKey) {
            setFilters({
                ...filters,
                Estado: parseInt(item.props.itemKey),
            });
            setSubmit(true);
        }
    };

    const onStatusDropdownChanged = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) : void => {
        if (option) {
            setFilters({
                ...filters,
                Estado: parseInt(option.key.toString()),
            });
        }
    };

    const onChangeBeginDate = (value: string) : void => {
        setFilters({
            ...filters,
            FechaDesde: value,
        });
    };

    const onChangeEndDate = (value: string) : void => {
        setFilters({
            ...filters,
            FechaHasta: value,
        });
    };

    const onChangeOperator = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) : void => {
        if (option) {
            const newIds = option.selected
                ? [...selectedOperatorIds, option.key as string]
                : selectedOperatorIds.filter((id: string) => id !== option.key);
            setSelectedOperatorIds(newIds);
            setFilters((f: ItemFiltrosOrdenDeTrabajo) => ({
                ...f,
                Operarios: newIds.join("|"),
            }));
        }
    };

    const onFilter = () : void => {
        setSubmit(true);
    };

    const onResetFilter = () : void => {
        setFilters(new ItemFiltrosOrdenDeTrabajo());
        setSubmit(true);
    };

    const onSelectWorkOrder = (workOrder: ItemOrdenDeTrabajo) => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemOrdenDeTrabajo>("OrdenesTrabajo", "Get", auth.user, {
                "IdOrden": workOrder.Id,
            }).then((response: ItemOrdenDeTrabajo) => {
                setSelectedWorkOrder(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
        else {
            setSelectedWorkOrder(undefined);
        }
    };

    return (
        (auth.user && workOrdersPermission.Ver) ? (
            <div className='d-flex flex-column h-100 w-100'>
                <div className={toolbarClasses.titleRow}>
                    <BreadcrumbComponent items={["Inicio", "Bandeja de entrada"]} />
                    <div className={toolbarClasses.titleRight}>
                        {workOrdersPermission.Nuevo ? (
                            <ButtonPrimaryComponent
                                text='Crear'
                                icon='AddTo'
                                onClick={() => setWorkOrderOpen(true)} />
                        ) : null}
                        <SearchBox
                            styles={searchBoxStyles}
                            placeholder='Buscar...'
                            value={searchText}
                            onChange={(_e, v) => setSearchText(v ?? '')}
                            onClear={() => setSearchText('')} />
                    </div>
                </div>
                <div className={toolbarClasses.pivotRow}>
                    <Pivot
                        styles={pivotStyles}
                        selectedKey={filters.Estado.toString()}
                        onLinkClick={onStatusPivotChanged}
                        headersOnly={true}>
                        <PivotItem itemKey="0" headerText='Todas' />
                        <PivotItem itemKey="10" headerText='Pendientes' />
                        <PivotItem itemKey="20" headerText='En curso' />
                        <PivotItem itemKey="30" headerText='Finalizadas' />
                        <PivotItem itemKey="40" headerText='Cerradas' />
                    </Pivot>
                    <ButtonPrimaryComponent
                        text='Filtros'
                        icon='Filter'
                        secondary={true}
                        onClick={() => setFiltersOpen(true)} />
                </div>
                <div className="row m-0 flex-fill overflow-hidden h-100">
                    <div className='col-12 col-md-4 mh-100 overflow-auto'>
                        <WorkOrderListComponent
                            onSelectWorkOrder={onSelectWorkOrder}
                            workOrders={workOrders.filter((wo: ItemOrdenDeTrabajo) =>
                                !searchText ||
                                wo.NumeroDocumento?.toLowerCase().includes(searchText.toLowerCase()) ||
                                wo.Nombre?.toLowerCase().includes(searchText.toLowerCase()) ||
                                wo.Descripcion?.toLowerCase().includes(searchText.toLowerCase())
                            )} />
                    </div>
                    <div className='col-12 col-md-8 mh-100 overflow-auto d-none d-md-block'>
                        {selectedWorkOrder ? (
                            <WorkOrderComponent
                                workOrder={selectedWorkOrder}
                                setLoading={setLoading} />
                        ) : (
                            <div className='d-flex flex-column align-items-center justify-content-center h-100 gap-3'>
                                <img className={toolbarClasses.emptyIcon} src={emptyIcon} alt="" height={64} width={64} />
                                <span className={toolbarClasses.emptyState}>
                                    Todavía no ha seleccionado una orden de trabajo
                                </span>
                            </div>
                        )}
                    </div>
                    {
                        selectedWorkOrder ? (
                            <ModalDefaultComponent
                                title="Orden de trabajo"
                                className='d-flex d-md-none'
                                onDismiss={() => setSelectedWorkOrder(undefined)}>
                                <WorkOrderComponent 
                                    workOrder={selectedWorkOrder}
                                    setLoading={setLoading} />
                            </ModalDefaultComponent>
                        ) : (
                            null
                        )
                    }
                </div>
                <Panel 
                    isOpen={filtersOpen} 
                    onDismiss={() => setFiltersOpen(false)} 
                    type={PanelType.smallFixedFar}
                    headerText="Filtros"
                    styles={panelFiltersStyles}>
                    <div className="container-fluid p-0">
                        <div className='row m-0'>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Haz una búsqueda más rápida y avanzada
                                </Text>
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Estado
                                </Text>
                                <Dropdown
                                    selectedKey={filters.Estado}
                                    onChange={onStatusDropdownChanged}
                                    options={stateOptions} />
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Intervalo de fechas
                                </Text>
                                <DatePickerComponent 
                                    value={filters.FechaDesde}
                                    onChange={onChangeBeginDate} />
                                <DatePickerComponent 
                                    value={filters.FechaHasta}
                                    onChange={onChangeEndDate} />
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Operarios
                                </Text>
                                <Dropdown
                                    defaultSelectedKeys={selectedOperatorIds}
                                    multiSelect
                                    onChange={onChangeOperator}
                                    options={operators.map((o: ItemOperario) => {
                                        return {
                                            key: o.IdUsuario,
                                            text: o.Nombre,
                                        };
                                    })}>
                                </Dropdown>
                            </div>
                        </div>
                        <div className='row mx-0 my-3 justify-content-between'>
                            <div className='col-auto p-0'>
                                <DefaultButton 
                                    type='submit'
                                    text='Aplicar filtros'
                                    onClick={onFilter} />
                            </div>
                            <div className='col-auto p-0'>
                                <DefaultButton 
                                    type='button'
                                    text='Restablecer filtros'
                                    onClick={onResetFilter} />
                            </div>
                        </div>
                    </div>
                </Panel>
                {
                    workOrderOpen ? (
                        <WorkOrderCreateComponent
                            isOpen={true}
                            onWorkOrderCreated={() => setFilters({...filters})}
                            onWorkOrderCreatedAndEdit={(id: number) => {
                                setFilters({...filters});
                                if (auth.user) {
                                    setLoading(true);
                                    ApiService.Post<ItemOrdenDeTrabajo>('OrdenesTrabajo', 'Get', auth.user, { IdOrden: id })
                                        .then((res: ItemOrdenDeTrabajo) => {
                                            setSelectedWorkOrder(res);
                                            setLoading(false);
                                        }, () => setLoading(false));
                                }
                            }}
                            onClose={() => setWorkOrderOpen(false)} />
                    ) : (
                        null
                    )
                }
                <ErrorMessageComponent message={error} />
                <LoaderComponent loading={loading} />
            </div>
        ) : (
            <NoPermissionComponent showButton={true} />
        )
    );
};

export default WorkOrdersPage;
